import React, { useEffect, useState, useRef } from "react";
import { AnalyticsData } from "../../api/Companies";
import { Scatter } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
  TooltipOptions,
  TooltipItem,
} from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";
import { _DeepPartialObject } from "chart.js/dist/types/utils";
import LoadingSpinner from "../../shared-components/LoadingSpinner";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  annotationPlugin
);

type Props = {
  analyticsData: AnalyticsData | undefined;
};

const CHART_CONSTANTS = {
  CANDIDATE_COLOR: "#FF5D22",
  JITTER_HEIGHT: 10.0,
  CANDIDATE_DOT_RADIUS: 7,
  OTHER_CANDIDATE_DOT_RADIUS: 4,
  BREATHING_ROOM_MULTIPLIER: 0.5,
  DISTRIBUTION_FACTOR: 0.8,
  GRID_TARGET_INTERVALS: 10,
  BORDER_WIDTH: {
    GRID: 1,
    CANDIDATE_LINE: 2,
  },
  OPACITY: {
    GRID: 0.1,
    X_AXIS_LINE: 0.2,
    OTHER_CANDIDATES_BG: 0.6,
    OTHER_CANDIDATES_BORDER: 0.9,
  },
  Z_INDEX: {
    GRID: 1,
    GRID_LABELS: 2,
    CANDIDATE_LINE: 300,
    CANDIDATE_LABEL: 400,
    X_AXIS_LINE: 500,
  },
  FONT_SIZE: 12,
  LABEL_PADDING: 4,
  BACKGROUND_COLOR: `rgba(50, 95, 197, 0.85)`,
  BORDER_COLOR: `rgba(50, 95, 197, 0.85)`,
};

const CHART_OFFSETS = {
  LINE_Y_MIN: -0.5,
  LINE_Y_MAX_OFFSET: 1.2,
  GRID_LINE_START_OFFSET: 0.8,
  LABEL_Y_OFFSET: 0.6,
  X_AXIS_LINE_Y_OFFSET: 1.2,
};

interface FlagStats {
  min: number;
  max: number;
}

interface ScatterPoint {
  x: number;
  y: number;
}

const generateScatterPoints = (
  flagDistribution: Record<string, number>
): ScatterPoint[] => {
  const jitterCenter = CHART_CONSTANTS.JITTER_HEIGHT / 2;
  const points: ScatterPoint[] = [];

  Object.keys(flagDistribution).forEach((flagCount) => {
    const count = flagDistribution[flagCount];
    if (count > 100) {
      flagDistribution[flagCount] = 100;
    }
  });

  for (const [flagCount, count] of Object.entries(flagDistribution)) {
    const x = Number(flagCount);

    for (let i = 0; i < count; i++) {
      let y;
      if (count === 1) {
        y = jitterCenter;
      } else {
        const stepSize = CHART_CONSTANTS.JITTER_HEIGHT / (count + 1);
        const offsetFromCenter =
          (i - (count - 1) / 2) *
          stepSize *
          CHART_CONSTANTS.DISTRIBUTION_FACTOR;
        y = jitterCenter + offsetFromCenter;
      }
      points.push({ x, y });
    }
  }

  return points;
};

const calculateAxisRange = (
  flagStats: FlagStats,
  candidateFlagCount: number,
  flagDistribution: Record<string, number>
) => {
  const breathingRoom =
    CHART_CONSTANTS.BREATHING_ROOM_MULTIPLIER *
    CHART_CONSTANTS.CANDIDATE_DOT_RADIUS;
  const minX = flagStats.min - breathingRoom;

  // Find the maximum flag count from the distribution points
  const maxDistributionFlag = Math.max(
    ...Object.keys(flagDistribution).map(Number)
  );

  const maxX = Math.max(maxDistributionFlag, candidateFlagCount) + 10;
  const lineYMin = CHART_OFFSETS.LINE_Y_MIN;
  const lineYMax =
    CHART_CONSTANTS.JITTER_HEIGHT + CHART_OFFSETS.LINE_Y_MAX_OFFSET;

  return { minX, maxX, lineYMin, lineYMax };
};

const calculateStepSize = (range: number): number => {
  let stepSize = Math.max(
    1,
    Math.round(range / CHART_CONSTANTS.GRID_TARGET_INTERVALS)
  );

  if (stepSize <= 1) return 1;
  if (stepSize <= 2) return 2;
  if (stepSize <= 5) return 5;
  if (stepSize <= 10) return 10;
  return Math.ceil(stepSize / 10) * 10;
};

const generateGridAnnotations = (
  minX: number,
  maxX: number,
  lineYMin: number,
  lineYMax: number
) => {
  const annotations: any = {};
  const range = maxX - minX;
  const stepSize = calculateStepSize(range);
  const startTick = Math.floor(minX / stepSize) * stepSize;

  for (let tick = startTick; tick <= maxX; tick += stepSize) {
    if (tick >= minX) {
      annotations[`gridLine${tick}`] = {
        type: "line",
        xMin: tick,
        xMax: tick,
        yMin: lineYMin + CHART_OFFSETS.GRID_LINE_START_OFFSET,
        yMax: lineYMax,
        borderColor: `rgba(0, 0, 0, ${CHART_CONSTANTS.OPACITY.GRID})`,
        borderWidth: CHART_CONSTANTS.BORDER_WIDTH.GRID,
        display: true,
        drawTime: "beforeDatasetsDraw",
        z: CHART_CONSTANTS.Z_INDEX.GRID,
      };

      annotations[`gridLabel${tick}`] = {
        type: "label",
        xValue: tick,
        yValue: lineYMin + CHART_OFFSETS.LABEL_Y_OFFSET,
        content: `${tick}`,
        color: "rgba(102, 102, 102, 1)",
        font: { size: CHART_CONSTANTS.FONT_SIZE },
        textAlign: "center",
        position: "center",
        display: true,
        drawTime: "beforeDatasetsDraw",
        z: CHART_CONSTANTS.Z_INDEX.GRID_LABELS,
      };
    }
  }

  return annotations;
};

const generateCandidateAnnotations = (
  candidateFlagCount: number,
  minX: number,
  maxX: number,
  lineYMin: number,
  lineYMax: number
) => {
  return {
    candidateLine: {
      type: "line",
      xMin: candidateFlagCount,
      xMax: candidateFlagCount,
      yMin: lineYMin + CHART_OFFSETS.GRID_LINE_START_OFFSET,
      yMax: lineYMax,
      borderColor: CHART_CONSTANTS.CANDIDATE_COLOR,
      borderWidth: CHART_CONSTANTS.BORDER_WIDTH.CANDIDATE_LINE,
      display: true,
      drawTime: "afterDatasetsDraw",
      z: CHART_CONSTANTS.Z_INDEX.CANDIDATE_LINE,
    },
    candidateLabel: {
      type: "label",
      xValue: candidateFlagCount,
      yValue: lineYMin + CHART_OFFSETS.LABEL_Y_OFFSET,
      backgroundColor: "white",
      color: CHART_CONSTANTS.CANDIDATE_COLOR,
      content: `${candidateFlagCount}`,
      font: {
        size: CHART_CONSTANTS.FONT_SIZE,
        weight: "bold",
      },
      padding: CHART_CONSTANTS.LABEL_PADDING,
      textAlign: "center",
      position: "center",
      display: true,
      drawTime: "afterDraw",
      z: CHART_CONSTANTS.Z_INDEX.CANDIDATE_LABEL,
    },
  };
};

const createDatasetConfiguration = (scatterPoints: ScatterPoint[]) => {
  return {
    datasets: [
      {
        label: "Other Candidates",
        data: scatterPoints,
        backgroundColor: CHART_CONSTANTS.BACKGROUND_COLOR,
        borderColor: CHART_CONSTANTS.BORDER_COLOR,
        pointRadius: CHART_CONSTANTS.OTHER_CANDIDATE_DOT_RADIUS,
        showLine: false,
      },
    ],
  };
};

const createScaleConfiguration = (
  minX: number,
  maxX: number,
  lineYMin: number,
  lineYMax: number
) => {
  return {
    x: {
      type: "linear" as const,
      position: "bottom" as const,
      title: { display: true, text: "Misconduct Flags" },
      min: minX,
      max: maxX,
      display: true,
      ticks: {
        display: false,
        color: "transparent",
        stepSize: 1,
      },
      grid: { display: false },
      border: { display: false },
    },
    y: {
      display: true,
      min: lineYMin,
      max: lineYMax,
      ticks: { display: false },
      grid: { display: false },
      border: { display: false },
    },
  };
};

const createTooltipConfiguration = (): _DeepPartialObject<
  TooltipOptions<"scatter">
> => {
  return {
    callbacks: {
      label: (ctx: TooltipItem<"scatter">) => {
        const x = ctx.parsed?.x;
        return ctx.dataset.label === "This Candidate"
          ? `This candidate — Flags: ${x}`
          : `Flags: ${x}`;
      },
    },
  };
};

const createChartConfiguration = (analyticsData: AnalyticsData) => {
  const scatterPoints = generateScatterPoints(analyticsData.adverse_flag_dist);
  const candidateFlagCount = analyticsData.candidate.flag_count;
  const { minX, maxX, lineYMin, lineYMax } = calculateAxisRange(
    analyticsData.adverse_flag_stats,
    candidateFlagCount,
    analyticsData.adverse_flag_dist
  );

  const gridAnnotations = generateGridAnnotations(
    minX,
    maxX,
    lineYMin,
    lineYMax
  );
  const candidateAnnotations = generateCandidateAnnotations(
    candidateFlagCount,
    minX,
    maxX,
    lineYMin,
    lineYMax
  );
  const data = createDatasetConfiguration(scatterPoints);
  const scales = createScaleConfiguration(minX, maxX, lineYMin, lineYMax);
  const tooltip = createTooltipConfiguration();

  const options: ChartOptions<"scatter"> = {
    responsive: true,
    maintainAspectRatio: false,
    resizeDelay: 0,
    plugins: {
      title: { display: false },
      legend: { display: false },
      tooltip,
      annotation: {
        annotations: {
          ...gridAnnotations,
          ...candidateAnnotations,
        },
      },
    },
    scales,
  };

  return { data, options };
};

const ReportsSummaryChartFlags = ({ analyticsData }: Props) => {
  const [chartData, setChartData] = useState<any>(null);
  const [chartKey, setChartKey] = useState(0);
  const [chartImageUrl, setChartImageUrl] = useState<string>("");
  const chartRef = useRef<ChartJS<"scatter">>(null);

  useEffect(() => {
    if (analyticsData) {
      const chartConfig = createChartConfiguration(analyticsData);
      setChartData(chartConfig);
    }
  }, [analyticsData]);

  useEffect(() => {
    const handleResize = () => {
      if (analyticsData) {
        const chartConfig = createChartConfiguration(analyticsData);
        setChartData(chartConfig);
        setChartKey((prev) => prev + 1);
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [analyticsData]);

  // Capture chart as data URL after it's rendered
  useEffect(() => {
    if (chartRef.current) {
      // Use a slight delay to ensure the chart is fully rendered
      const timer = setTimeout(() => {
        if (chartRef.current) {
          const canvas = chartRef.current.canvas;
          const imageUrl = canvas.toDataURL("image/png");
          setChartImageUrl(imageUrl);
        }
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [chartData, chartKey]);

  return (
    <>
      <div className="w-full h-full print:hidden">
        {!chartData && <LoadingSpinner />}
        {chartData && (
          <Scatter
            key={chartKey}
            ref={chartRef}
            data={chartData.data}
            options={chartData.options}
          />
        )}
      </div>
      <div className="hidden print:flex items-center justify-center w-full h-full">
        {chartImageUrl && (
          <img
            src={chartImageUrl}
            alt="Flags Distribution Chart"
            className="h-48 w-96 object-contain"
          />
        )}
      </div>
    </>
  );
};

export default ReportsSummaryChartFlags;
